import { useEffect, useId, useRef, useState } from 'react'
import { ChevronRightIcon, TrashIcon } from './icons'
import { formatMoney } from '../lib/format'
import { unconfirmedPriceLabel } from '../lib/vehiclePricing'
import type { BasketItem } from '../lib/services'
import type { TotalDisplay } from '../lib/services'

/** The chosen tyre, which is not a channel service and so not a basket item. */
export interface BasketTyreLine {
  description: string
  /** 0 while no supplier can price it. */
  total: number
}

export interface BasketBarProps {
  basket: BasketItem[]
  tyreLine: BasketTyreLine | null
  total: TotalDisplay
  showPrices: boolean
  onRemoveService: (serviceId: string) => void
}

/**
 * The booking so far, on every screen.
 *
 * One line when closed — "3 items · £182.85" — expanding upward into the list.
 * It replaces three hand-rolled summaries that each showed a different subset on
 * a different step, which meant the answer to "what am I paying for?" changed
 * depending on where you were standing.
 *
 * Collapsed by default because a phone has no room to spend on a list the
 * customer already knows: they just chose it. The value of always showing it is
 * the running total and the ability to undo, not the inventory.
 */
export function BasketBar({
  basket,
  tyreLine,
  total,
  showPrices,
  onRemoveService,
}: BasketBarProps) {
  const [open, setOpen] = useState(false)
  const [bumped, setBumped] = useState(false)
  const panelId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const previousCount = useRef(0)

  const lineCount = basket.length + (tyreLine ? 1 : 0)

  /**
   * A flash on the total when a line is added.
   *
   * The build surface scrolls, so a customer adding something near the bottom
   * changes a number they may not be able to see. Without the flash the tap has
   * no visible consequence at all, which reads as nothing having happened.
   */
  useEffect(() => {
    const grew = lineCount > previousCount.current
    previousCount.current = lineCount
    if (!grew) return

    setBumped(true)
    const timer = setTimeout(() => setBumped(false), 450)
    return () => clearTimeout(timer)
  }, [lineCount])

  // Escape closes the sheet without closing the booking panel behind it.
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
    }

    const node = containerRef.current
    node?.addEventListener('keydown', handleKeyDown)
    return () => node?.removeEventListener('keydown', handleKeyDown)
  }, [open])

  // Nothing chosen yet, or a channel that hides prices entirely.
  if (lineCount === 0) return null

  return (
    <div className={`ac-basket${bumped ? ' ac-basket--bumped' : ''}`} ref={containerRef}>
      {open ? (
        <div className="ac-basket__sheet" id={panelId}>
          <div className="ac-basket__lines">
            {basket.map((item) => (
              <div key={item.serviceId} className="ac-basket__line">
                <span className="ac-basket__name">{item.name}</span>
                <span className="ac-basket__value">
                  {showPrices ? (
                    <span className="ac-basket__price">
                      {item.included
                        ? 'Included'
                        : item.priceState === 'afterReg'
                          ? 'Priced after your reg'
                          : item.priceState
                            ? unconfirmedPriceLabel(item.priceState)
                            : item.price == null
                              ? 'Quoted at the garage'
                              : item.price > 0
                                ? formatMoney(item.price)
                                : 'Free'}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="ac-basket__remove"
                    onClick={() => onRemoveService(item.serviceId)}
                    aria-label={`Remove ${item.name}`}
                  >
                    <TrashIcon size={15} />
                  </button>
                </span>
              </div>
            ))}

            {tyreLine ? (
              <div className="ac-basket__line">
                <span className="ac-basket__name">{tyreLine.description}</span>
                {showPrices ? (
                  <span className="ac-basket__value">
                    <span className="ac-basket__price">
                      {tyreLine.total > 0 ? formatMoney(tyreLine.total) : 'Priced at the garage'}
                    </span>
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className={`ac-basket__bar${open ? ' ac-basket__bar--open' : ''}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span className="ac-basket__count">
          {lineCount} item{lineCount === 1 ? '' : 's'}
        </span>

        {/* No figure at all while one is unknown, e.g. an MOT waiting for "What is it?" (ACA-110). */}
        {showPrices && total.value ? (
          <span className="ac-basket__total">
            {total.lead ? (
              <>
                <span className="ac-basket__figure">
                  <span className="ac-basket__total-label">{total.lead.label}</span>
                  <strong>{total.lead.value}</strong>
                </span>
                <span className="ac-basket__dot" aria-hidden="true">
                  ·
                </span>
              </>
            ) : null}
            <span className="ac-basket__figure">
              <span className="ac-basket__total-label">{total.label}</span>
              <strong>{total.value}</strong>
            </span>
          </span>
        ) : null}

        <span
          className={`ac-basket__chevron${open ? ' ac-basket__chevron--open' : ''}`}
          aria-hidden="true"
        >
          <ChevronRightIcon size={16} />
        </span>
      </button>

      {showPrices && total.caption ? (
        <span className="ac-basket__caption">{total.caption}</span>
      ) : null}
    </div>
  )
}
